import {
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  TypographyVariant,
  useTheme,
} from '@mui/material'
import { NumberParts } from 'components/Common/Input/NumberParts'
import {
  getUnitDecimalPrecision,
  useSelectableAbbreviations,
} from 'features/measurementSystem/utils/selectableAbbreviations'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuantityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  DefinitiveDecimalTextField,
  DefinitiveDecimalTextFieldProps,
} from './DefinitiveDecimalTextField'

export type DefinitiveQuantityFieldProps = {
  quantityDto: QuantityDto
  /**
   * @param value the new QuantityDto value
   * @param isValid if the value is valid according to the validation function
   * @param validations current validation errors
   * @returns
   */
  onChange: (
    value: QuantityDto,
    isValid?: boolean,
    validations?: string[],
  ) => void
  validation?: (value: QuantityDto) => string[]
  onValidation?: (validations: string[]) => void
  showRawValueInTooltip?: (value: QuantityDto) => string
  typographyVariant?: TypographyVariant
  maxValue?: number
  disabledUnit?: boolean
  disabledValue?: boolean
  showThousandsSeparator?: boolean
  /**
   * will fire onChange if there are validation errors
   */
  onChangeIfInvalid?: boolean
} & Omit<DefinitiveDecimalTextFieldProps, 'value' | 'onChange'>

export const DefinitiveQuantityField = (
  props: DefinitiveQuantityFieldProps,
) => {
  const [currentQuantity, setCurrentQuantity] = useState(props.quantityDto)
  const [currentValidations, setCurrentValidations] =
    useState<string[]>(undefined)

  const selectableAbbreviations = useSelectableAbbreviations(
    props.quantityDto?.quantityType,
  )

  const { onChange, onValidation, validation, quantityDto, onChangeIfInvalid } =
    props

  const { i18n } = useTranslation()

  const decimalSeparator = useMemo(() => {
    return NumberParts(i18n.language, 'decimal')
  }, [i18n.language])

  const thousandSeparator = useMemo(() => {
    return NumberParts(i18n.language, 'group')
  }, [i18n.language])

  const theme = useTheme()

  useEffect(() => {
    setCurrentQuantity(quantityDto)

    const validations = validation ? validation(quantityDto) : undefined

    if (validations?.length > 0) {
      setCurrentValidations(validations)
    } else {
      setCurrentValidations(undefined)
    }
  }, [onChange, onChangeIfInvalid, onValidation, quantityDto, validation])

  const handleChangeValue = useCallback(
    (value: number) => {
      if (value === currentQuantity.value) {
        return
      }

      const newQuantity = {
        ...currentQuantity,
      }

      newQuantity.value = value

      setCurrentQuantity(newQuantity)

      const validations = props.validation
        ? props.validation(newQuantity)
        : undefined

      if (validations?.length > 0) {
        setCurrentValidations(validations)
      } else {
        setCurrentValidations(undefined)
      }

      props.onValidation?.(validations)

      if (props.onChangeIfInvalid || !validations?.length) {
        props.onChange(newQuantity, !validations?.length, validations)
      }
    },
    [currentQuantity, props],
  )

  const handleChangeUnit = useCallback(
    (unit: string) => {
      const newQuantity = {
        ...currentQuantity,
      }

      newQuantity.unit = unit

      setCurrentQuantity(newQuantity)

      const validations = validation ? validation(newQuantity) : undefined

      if (validations?.length > 0) {
        setCurrentValidations(validations)
      } else {
        setCurrentValidations(undefined)
      }

      if (props.onChangeIfInvalid || !validations?.length) {
        onChange(newQuantity, !validations?.length, validations)
      }

      onValidation?.(validations)
    },
    [
      currentQuantity,
      onChange,
      onValidation,
      props.onChangeIfInvalid,
      validation,
    ],
  )

  if (!props.quantityDto || !currentQuantity) {
    return null
  }

  return (
    <>
      <DefinitiveDecimalTextField
        {...props}
        numericFormatProps={{
          decimalScale:
            props.numericFormatProps?.decimalScale ??
            getUnitDecimalPrecision(
              currentQuantity.unit,
              props.numericFormatProps?.decimalScale ?? 2,
            ),
          fixedDecimalScale: true,
          allowNegative: props.numericFormatProps?.allowNegative ?? false,
          decimalSeparator: decimalSeparator,
          thousandSeparator: props.showThousandsSeparator
            ? thousandSeparator
            : '',
          ...props.numericFormatProps,
        }}
        textFieldProps={{
          ...(props.textFieldProps || {}),
          disabled: props.textFieldProps?.disabled ?? props.disabledValue,
          inputProps: {
            ...props.textFieldProps?.inputProps,
            style: {
              textAlign:
                props.textFieldProps?.inputProps?.style?.textAlign || 'right',
              fontSize:
                theme.typography[props.typographyVariant || 'caption'].fontSize,
              padding:
                props.textFieldProps?.inputProps?.style?.padding ||
                '2px 0 2px 0',
              ...(props.textFieldProps?.inputProps?.style || {}),
              minWidth: '3rem',
              width: '100%',
            },
          },
          InputProps: {
            endAdornment: selectableAbbreviations.length && (
              <InputAdornment position="end" style={{ maxHeight: '0' }}>
                {props.disabledUnit || selectableAbbreviations?.length < 2 ? (
                  <Typography
                    variant={props.typographyVariant || 'caption'}
                    color="textSecondary"
                  >
                    {currentQuantity?.unit}
                  </Typography>
                ) : (
                  <Select
                    value={currentQuantity?.unit}
                    onChange={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      handleChangeUnit(e.target.value as string)
                    }}
                    disabled={
                      props.disabledUnit || selectableAbbreviations?.length < 2
                    }
                    error={currentValidations?.length > 0}
                    color={
                      currentValidations?.length > 0 ? 'secondary' : 'primary'
                    }
                    sx={{
                      fontSize:
                        theme.typography[props.typographyVariant || 'caption']
                          .fontSize,
                      minWidth: '5em',
                      '& .MuiSelect-select': {
                        padding: 0,
                      },
                      '& input': {
                        padding: 0,
                      },
                    }}
                    disableUnderline
                    readOnly={props.disabledUnit}
                  >
                    {selectableAbbreviations.map((abbreviation) => (
                      <MenuItem key={abbreviation} value={abbreviation}>
                        {abbreviation}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </InputAdornment>
            ),
            ...props.textFieldProps?.InputProps,
          },
        }}
        maxValue={props.maxValue}
        value={currentQuantity?.value}
        onChange={(value) => {
          handleChangeValue(value)
        }}
        warnings={currentValidations}
        showRawValueInTooltip={props.showRawValueInTooltip}
      />
    </>
  )
}
